import React from 'reactn';
import PropTypes from 'prop-types';
import { Dialog, Box, IconButton, AppBar, Toolbar, Typography, DialogContent, Grid, CircularProgress } from '@material-ui/core';
import { Menu } from '../management';
import { getUsersList, submitEditUserCard } from '../../util/APIUtils';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import EditIcon from '@material-ui/icons/Edit';
import ReadView from './read-view/read-view';
import EditView from './edit-view/edit-view';
import SaveIcon from '@material-ui/icons/Save';
import { Formik } from 'formik';
import { PDFDownloadLink } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import moment from 'moment';
import { USER_CARD_DATETIME_FORMAT } from '../../constants';
import { Packer } from "docx";
import Pdf from './pdf'
import Word from './word'
import { Loader } from '@orijinworks/frontend-commons';
import { Alert } from '@material-ui/lab';
import { validatePassword } from '../../util/Helpers';
import { hydrate } from 'react-dom';


const CSV = 0;
const PDF = 1;
const WORD = 2;

/** @param {string[]} ids @param {number} chunkSize */
const chunkIds = (ids, chunkSize) => {
    const batches = [];
    for (let i = 0; i < ids.length; i += chunkSize) {
        batches.push(ids.slice(i, i + chunkSize));
    }
    return batches;
};


class BulkUserCard extends React.Component {
    formRef = null;
    BATCH_SIZE = 100;
    CONCURRENT_REQUEST_COUNT = 2;
    cacheUserData = null;
    /** @type {Map<string, object>} deep-cloned originals keyed by userDetail.userId */
    cacheByUserId = new Map();
    _fetchGeneration = 0;
    VIEW_TYPES = {
        READ_ONLY: 0,
        EDITABLE: 1
    };
    currentTime = null;
    FIELDS_CHANGE = {};
    state = {
        isCompleted: false,
        users: [],
        viewType: this.VIEW_TYPES.READ_ONLY,
        loading: false,
        triggerDownload: false,
        downloadType: "",
        PDFUrl: null,
        loaderText: "Please wait, content is being downloaded!",
        PDFError: null,
        PDFLoadedCount: 0,
        isFetchingCards: false,
        cardsFetchFinished: false,
        cardsLoadError: null,
    };

    /**
     * Runs when the dialog becomes open (including first mount with open=true, e.g. batched setState).
     */
    startFetchWhenDialogOpens = (prevProps) => {
        const becameOpen =
            this.props.isOpen && (!prevProps || !prevProps.isOpen);
        if (!becameOpen) {
            return;
        }
        this.setState({
            PDFUrl: null,
            PDFError: null,
            PDFLoadedCount: 0,
        });
        if (
            this.state.users.length === 0 &&
            !this.global.isLoading
        ) {
            this.getUserData(
                this.props.users.map((user) => user.personId)
            );
        }
    };

    componentDidMount() {
        this.startFetchWhenDialogOpens(null);
    }

    componentDidUpdate(prevProps) {
        this.startFetchWhenDialogOpens(prevProps);

        if (this.state.triggerDownload) {
            if (this.state.downloadType === CSV) {
                this.setState({
                    triggerDownload: false,
                }, () => { setTimeout(this.exportAsCsv, 0) });
            }
            if (this.state.downloadType === WORD) {
                this.setState({
                    triggerDownload: false,
                }, this.exportAsWord);
            }
            if (this.state.downloadType === PDF) {
                this.setState({
                    triggerDownload: false,
                }, this.exportAsPdf);
            }
        }

    };

    fieldChangeDetector = (userId, serviceId, field, value) => {
        if (this.FIELDS_CHANGE.hasOwnProperty(userId) && this.FIELDS_CHANGE[userId].hasOwnProperty(serviceId)) {
            this.FIELDS_CHANGE[userId][serviceId][field] = value;
        } else {
            this.FIELDS_CHANGE[userId] = {
                [serviceId]: {
                    [field]: value
                }
            };
        };
    };

    getStatusMsg = (statusCode, serviceId, userId) => {
        const userData = this.getServiceAndUsername(serviceId, userId);
        if (statusCode === 0) {
            return `${userData.serviceName} has been successfully updated for ${userData.userName}.`;
        } else {
            return `Something went wrong while updating ${userData.userName}.`;
        };
    };

    /**
     * @param {number} serviceKey
     * @param {number} userId
     * @desc Finds serivceName based upon userId & serviceKey
     * @return {string}
     */
    getServiceAndUsername = (serviceKey, userId) => {
        const user = this.state.users.find(
            _user => _user.userDetail.userId === userId
        );

        if (user) {
            const service = user.userServices.find(
                _service => _service.serviceKey === serviceKey
            );
            if (service) {
                return {
                    serviceName: service.serviceName,
                    userName: user.userDetail.userName
                };
            }
            // RETURNING ONLY 
            // USER DATA IF SERVICE
            // IS NOT FOUND
            return {
                userName: user.username
            };
        }
        return {};
    };

    /**
     * @param {string} msg
     * @desc Parses error msg based upon underlying logic.
     * @return {string}
     */
    parseErrorMsg = (msg, serviceKey, userId) => {
        const userData = this.getServiceAndUsername(serviceKey, userId);
        if (msg) {
            // REPLACING USERNAME
            msg = msg
                .split("null")
                .join(userData.userName);
            // REPLACING SERVICE NAME
            msg = msg
                .split("[Service_Name]")
                .join(userData.serviceName);
        }

        return msg;
    };

    /**
     * @name getResponseBoxData
     * @param {object} response
     * @desc Prepares response box data.
     * @return {object} data
     */
    getResponseData = (response) => {
        const data = {
            isOpen: true,
            data: []
        };
        response.successList.forEach(
            (_successNodes) => {
                _successNodes
                    .userServices
                    .forEach(
                        (_userServiceSuccess) => {
                            data.data.push({
                                statusCode: _userServiceSuccess.statusCode,
                                text: this.getStatusMsg(_userServiceSuccess.statusCode, _userServiceSuccess.serviceKey, _successNodes.UserId)
                            });
                        }
                    );
            }
        );
        response.errorList.forEach(
            (_errorNodes) => {
                _errorNodes.userServices
                    .forEach(
                        (_userServiceError) => {
                            data.data.push({
                                statusCode: _userServiceError.statusCode,
                                text: this.parseErrorMsg(_userServiceError.statusMessage, _userServiceError.serviceKey, _errorNodes.UserId)
                            });
                        }
                    );

            }
        );
        return data;
    };

    getCacheServiceCopy = (user, service) => {
        const originalUserData = this.cacheUserData.find(
            _user => _user.userDetail.userId === user.userDetail.userId
        );
        if (!originalUserData) {
            return null;
        }
        const originalServiceData = originalUserData.userServices.find(
            _service => _service.serviceKey === service.serviceKey
        );
        return originalServiceData;
    };
    /**
     * @name handleSubmit
     * @param {array} values
     * @desc Handles submit event of bulk Learner card
     * @return {void}
     */
    handleSubmit = (values) => {
        const requestPayload = values.users
            .filter(user => this.FIELDS_CHANGE.hasOwnProperty(parseInt(user.userDetail.userId)))
            .map(
                (user) => {
                    return {
                        UserId: parseInt(user.userDetail.userId),
                        userServices: user.userServices // FILTERING OUT NOT SSO ENABLED SERVICES
                            .filter(service => {
                                const userChangedObj = this.FIELDS_CHANGE[parseInt(user.userDetail.userId)];
                                const serviceExist = userChangedObj.hasOwnProperty(service.serviceKey);
                                if (serviceExist) {
                                    const originalServiceData = this.getCacheServiceCopy(user, service);
                                    if (!originalServiceData) {
                                        return false;
                                    }
                                    const isFieldChanged = Object.keys(userChangedObj[service.serviceKey])
                                        .some(
                                            (fieldKey) =>
                                                originalServiceData[fieldKey] !==
                                                userChangedObj[service.serviceKey][fieldKey]
                                        );

                                    return isFieldChanged;
                                }
                                return false;
                            })
                            .map(
                                service => ({
                                    serviceKey: service.serviceKey,
                                    active: !!service.active || false,
                                    externalUserName: service.externalUserName,
                                    externalUserPassword: service.externalUserPassword,
                                    externalId: service.externalId,
                                })
                            )
                    };
                }
            );
        if (requestPayload.length === 0) {
            this.handleClose();
            return;
        }
        this.setGlobal({
            isLoading: true
        });
        this.toggleView();
        submitEditUserCard(requestPayload)
            .then(
                (_successLog) => {
                    this.setGlobal({
                        isLoading: false
                    });

                    this.props.setResponse({
                        heading: "Learner Card Update",
                        ...this.getResponseData(_successLog)
                    });
                    if (this.props.fetchUsers) {
                        this.props.fetchUsers();
                    }
                    this.handleClose();
                },
                (_errorLog) => {
                    this.setGlobal({
                        isLoading: false
                    });
                }
            );

        // this.toggleView()
    };

    /**
     * @name toggleView
     * @desc Toggles view based upon existing state & updates to new opposite one.
     * @return {void}
     */
    toggleView = () => {
        const { viewType } = this.state;
        let newType = null;
        if (viewType === this.VIEW_TYPES.READ_ONLY) {
            newType = this.VIEW_TYPES.EDITABLE;
        } else if (viewType === this.VIEW_TYPES.EDITABLE) {
            newType = this.VIEW_TYPES.READ_ONLY;
        }
        this.setState({
            viewType: newType
        }, () => {
            if (newType === this.VIEW_TYPES.EDITABLE) {
                const serviceContainerElem = document.querySelector(".service-container");
                if (serviceContainerElem) {
                    setTimeout(
                        () => {
                            serviceContainerElem
                                .scrollIntoView({
                                    behavior: "smooth",
                                    block: "center"
                                });
                        }, 500
                    );
                }

            }
        });
    };

    /**
     * Merges newly fetched cards with prior state and orders by requested person ids.
     */
    buildOrderedUsers = (prevUsers, flatCards, orderedPersonIds) => {
        const byId = new Map();
        prevUsers.forEach((u) => {
            if (u && u.userDetail) {
                byId.set(String(u.userDetail.userId), u);
            }
        });
        flatCards.forEach((card) => {
            if (card && card.userDetail) {
                byId.set(String(card.userDetail.userId), card);
            }
        });
        const ordered = [];
        orderedPersonIds.forEach((pid) => {
            const id = String(pid);
            if (byId.has(id)) {
                ordered.push(byId.get(id));
            }
        });
        return ordered;
    };

    _takeNextBatchWave = (batches, startIdx) => {
        const wave = [];
        let idx = startIdx;
        while (
            wave.length < this.CONCURRENT_REQUEST_COUNT &&
            idx < batches.length
        ) {
            wave.push(batches[idx]);
            idx += 1;
        }
        return { wave, nextIdx: idx };
    };

    _userCardsFromSettledResults = (results) => {
        const newCards = [];
        results.forEach((result) => {
            if (
                result.status === "fulfilled" &&
                result.value &&
                Array.isArray(result.value.userCards)
            ) {
                newCards.push(...result.value.userCards);
            } else if (result.status === "rejected") {
                // eslint-disable-next-line no-console -- surface failed batch for support/debugging
                console.error("getUsersList failed", result.reason);
            }
        });
        return newCards;
    };

    _cacheFetchedUserCards = (newCards) => {
        newCards.forEach((card) => {
            if (card && card.userDetail) {
                const uid = String(card.userDetail.userId);
                this.cacheByUserId.set(uid, JSON.parse(JSON.stringify(card)));
            }
        });
    };

    _bulkFetchLoaderCopy = ({
        loadedCount,
        expectedTotal,
        isFinalWave,
        estimatedMins,
    }) => {
        if (loadedCount === expectedTotal) {
            return {
                loaderText: "All cards have been downloaded successfully!",
                isCompleted: true,
            };
        }
        if (isFinalWave) {
            return {
                loaderText:
                    loadedCount > 0
                        ? `Loaded ${loadedCount} of ${expectedTotal} learner cards. Some learners may be missing or could not be loaded.`
                        : "Unable to load learner cards. Please try again.",
                isCompleted: false,
            };
        }
        return {
            loaderText: `Please wait, content is being downloaded. Estimated time ~ ${estimatedMins.toFixed(2)} minutes.`,
            isCompleted: false,
        };
    };

    /**
     * @returns {null | { nextOrderedUsers: object[], stateUpdate: object }}
     */
    _resolveBulkFetchWaveState = (prevState, {
        newCards,
        orderedPersonIds,
        expectedTotal,
        isFinalWave,
        completedBatchCount,
        totalBatches,
        isGenerationStale,
    }) => {
        if (isGenerationStale()) {
            return null;
        }
        const nextOrderedUsers = this.buildOrderedUsers(
            prevState.users,
            newCards,
            orderedPersonIds
        );
        const loadedCount = nextOrderedUsers.length;
        const downloadComplete = loadedCount === expectedTotal;
        const terminal = isFinalWave || downloadComplete;
        const avgSecPerBatch =
            completedBatchCount > 0
                ? moment().diff(this.currentTime, "seconds") / completedBatchCount
                : 0;
        const estimatedMins = (avgSecPerBatch * totalBatches) / 60;
        const { loaderText, isCompleted } = this._bulkFetchLoaderCopy({
            loadedCount,
            expectedTotal,
            isFinalWave,
            estimatedMins,
        });
        return {
            nextOrderedUsers,
            stateUpdate: {
                users: nextOrderedUsers,
                loaderText,
                isCompleted,
                cardsFetchFinished: terminal,
                isFetchingCards: !terminal,
                cardsLoadError:
                    isFinalWave && loadedCount === 0
                        ? new Error("bulk_card_fetch_empty")
                        : null,
            },
        };
    };

    _applyWaveFetchCompletion = (nextOrderedUsers, resolve) => {
        this.cacheUserData = nextOrderedUsers.map((u) =>
            this.cacheByUserId.get(String(u.userDetail.userId))
        );
        if (this.formRef) {
            this.formRef.setFieldValue("users", nextOrderedUsers);
        }
        this.setGlobal({ isLoading: false });
        resolve();
    };

    /**
     * Fetches user cards in waves of CONCURRENT_REQUEST_COUNT API calls; merges with functional updates.
     */
    runFetchWaves = async (orderedPersonIds, fetchGeneration) => {
        const expectedTotal = orderedPersonIds.length;
        const batches = chunkIds(orderedPersonIds, this.BATCH_SIZE);
        const totalBatches = Math.max(1, batches.length);
        let batchCursor = 0;
        let completedBatchCount = 0;

        const isGenerationStale = () => fetchGeneration !== this._fetchGeneration;

        while (batchCursor < batches.length) {
            if (isGenerationStale()) return;

            const { wave: currentWave, nextIdx } = this._takeNextBatchWave(
                batches,
                batchCursor
            );
            batchCursor = nextIdx;

            const results = await Promise.allSettled(
                currentWave.map((studentIds) => getUsersList({ studentIds }))
            );

            if (isGenerationStale()) return;

            const newCards = this._userCardsFromSettledResults(results);
            this._cacheFetchedUserCards(newCards);

            completedBatchCount += currentWave.length;
            const isFinalWave = batchCursor >= batches.length;

            let nextOrderedUsers = [];
            const waveContext = {
                newCards,
                orderedPersonIds,
                expectedTotal,
                isFinalWave,
                completedBatchCount,
                totalBatches,
                isGenerationStale,
            };

            await new Promise((resolve) => {
                this.setState(
                    (prevState) => {
                        const resolved = this._resolveBulkFetchWaveState(
                            prevState,
                            waveContext
                        );
                        if (!resolved) {
                            return prevState;
                        }
                        nextOrderedUsers = resolved.nextOrderedUsers;
                        return resolved.stateUpdate;
                    },
                    () => {
                        if (isGenerationStale()) {
                            resolve();
                            return;
                        }
                        this._applyWaveFetchCompletion(nextOrderedUsers, resolve);
                    }
                );
            });

            if (isGenerationStale()) return;
            if (nextOrderedUsers.length === expectedTotal) break;
        }
    };

    /**
     * @name getUserData
     * @param {array} userIds
     * @desc Fetches user data based upon user IDs passed into params.
     * @return {void}
     */
    getUserData = (userIds = []) => {
        this._fetchGeneration += 1;
        const fetchGeneration = this._fetchGeneration;
        this.currentTime = moment();
        this.cacheByUserId = new Map();
        this.cacheUserData = null;

        if (!userIds.length) {
            this.setGlobal({
                isLoading: false,
            });
            this.setState({
                isFetchingCards: false,
                cardsFetchFinished: true,
                cardsLoadError: null,
                isCompleted: false,
                users: [],
                loaderText: "No learners selected.",
            });
            return;
        }

        this.setGlobal({
            isLoading: true,
        });
        this.setState({
            isFetchingCards: true,
            cardsFetchFinished: false,
            cardsLoadError: null,
            isCompleted: false,
            users: [],
            loaderText: "Please wait, content is being downloaded!",
        });

        const run = async () => {
            try {
                await this.runFetchWaves(userIds, fetchGeneration);
                if (fetchGeneration !== this._fetchGeneration) {
                    return;
                }
            } catch (e) {
                console.error(e);
                if (fetchGeneration !== this._fetchGeneration) {
                    return;
                }
                this.setGlobal({
                    isLoading: false,
                });
                this.setState({
                    isFetchingCards: false,
                    cardsFetchFinished: true,
                    cardsLoadError: e,
                    loaderText:
                        "Unable to load learner cards. Please try again.",
                });
            }
        };
        run();
    };

    /**
     * @name getUserLocationCSVString
     * @param {array} userLocations
     * @desc Prepares user location CSV string as per 
     * userLocations passed.
     * @return {string}
     */
    getUserLocationCSVString = (userLocations = [], userServices = []) => {
        let csvString = "";
        userLocations.forEach(
            (locationData, index) => {
                csvString += `name: ${locationData.locationName} role type: ${locationData.roleTypeName} active: ${locationData.active ? "active" : "inactive"} start date: ${locationData.startDate ? moment.parseZone(locationData.startDate).format(USER_CARD_DATETIME_FORMAT) : "n/a"}, ${this.getServiceCSVAtIndex(index, userServices)}
                , , , , , , ,`;
            }
        );
        return csvString;
    };

    /**
     * @name getServiceCSVAtIndex
     * @param {number} index
     * @param {array} services
     * @desc Prepares string for CSV at index provided in param.
     * @return {string}
     */
    getServiceCSVAtIndex = (index, services) => {
        if (services.length <= index) {
            return "";
        }
        let csvString = `service name: ${services[index].serviceName} service display name: ${services[index].serviceDisplayName} sso enabled: ${services[index].ssoEnabled ? "enabled" : "disabled"} automated provisioning: ${services[index].automatedProvisioning ? "Yes" : "No"} active: ${services[index].active} external username: ${services[index].externalUserName || "n/a"} external password: ${services[index].externalUserPassword || "n/a"} start date: ${services[index].startDate ? moment.parseZone(services[index].startDate).format(USER_CARD_DATETIME_FORMAT) : "n/a"}`;
        return csvString;
    };

    /**
     * @name getUserServiceCSVString
     * @param {array} userLocations
     * @param {number} startIndex Index from which this needs to be started from.
     * @desc Prepares data for CSV and it starts from last index of location in 
     * order to avoid duplicate data.
     * @return {string}
     */
    getUserServiceCSVString = (userServices = [], startIndex) => {
        let csvString = "";

        for (let a = startIndex; a < userServices.length; a++) {
            csvString += ` service name: ${userServices[a].serviceName} service display name: ${userServices[a].serviceDisplayName} sso enabled: ${userServices[a].ssoEnabled ? "enabled" : "disabled"} automated provisioning: ${userServices[a].automatedProvisioning ? "Yes" : "No"} active: ${userServices[a].active} external username: ${userServices[a].externalUserName || "n/a"} external password: ${userServices[a].externalUserPassword || "n/a"} start date: ${userServices[a].startDate ? moment.parseZone(userServices[a].startDate).format(USER_CARD_DATETIME_FORMAT) : "n/a"}
            , , , , , , , ,`;

        }
        return csvString;
    };

    /**
     * @param {object} user
     * @param {object} service
     * @return {object|null} serviceError object or null if no errors
     */
    validateOneNonSsoService = (user, service) => {
        const skipKeys = new Set([124, 125]);
        if (skipKeys.has(service.serviceKey) || service.ssoEnabled) {
            return null;
        }
        const originalServiceData = this.getCacheServiceCopy(user, service);
        if (!originalServiceData) {
            return null;
        }
        const serviceError = {};
        if ((!service.externalUserName || service.externalUserName === "") && originalServiceData.externalUserName !== service.externalUserName) {
            serviceError.externalUserName = "Username is required.";
        }
        if ((!service.externalUserPassword || service.externalUserPassword === "") && originalServiceData.externalUserPassword !== service.externalUserPassword) {
            serviceError.externalUserPassword = "Password is required.";
        }
        if (service.externalUserPassword && originalServiceData.externalUserPassword !== service.externalUserPassword) {
            const passwordErrors = validatePassword(service.externalUserPassword);
            if (passwordErrors.length > 0) {
                serviceError.externalUserPassword = passwordErrors.join(", ");
            }
        }
        return Object.keys(serviceError).length > 0 ? serviceError : null;
    };

    /**
     * @name validateUserService
     * @param {object} user
     * @desc Validates user services based upon validation conditions.
     * @return {array | void}
     */
    validateUserService = (user) => {
        const errors = [];
        user
            .userServices
            .filter(service => service.active === "true")
            .forEach(
                (service, index) => {
                    const serviceError = this.validateOneNonSsoService(user, service);
                    if (serviceError) {
                        errors[index] = serviceError;
                    }
                }
            );
        return errors;
    };

    /**
     * @name validateForm
     * @param {object} values
     * @desc Validate's form against value and based upon certain conditions.
     * @return {object}
     */
    validateForm = (values) => {
        const errors = {};
        values.users
            .forEach(
                (user, index) => {
                    const userServiceErrors = this.validateUserService(user);
                    if (userServiceErrors.length > 0) {
                        if (!errors.hasOwnProperty("users")) {
                            errors["users"] = [];
                        }
                        errors["users"][index] = {
                            userServices: userServiceErrors
                        };
                    }

                }
            );
        return errors;

    };

    /**
     * @name exportAsCSV
     * @desc Exports entire data as CSV.
     * @return {void}
     */
    exportAsCsv = () => {
        try {
            const universalBOM = "\uFEFF";
            let csvContent = "";
            const mat = [];
            const headings = [
                "First Name",
                "Last Name",
                "Username",
                "Doc ID",
                "Active",
                "Location",
                "Service",
                "Username",
                "Password"
            ];

            mat.push(headings);

            this.state.users.forEach((user, index) => {
                const data = [
                    user.userDetail.firstName,
                    user.userDetail.lastName,
                    user.userDetail.userName,
                    user.userDetail.docId,
                    user.userDetail.active ? "active" : "inactive",
                    user.userLocations.length > 0 ? user.userLocations[0].locationName : "N/A",
                    "Connected Corrections",
                    user.userDetail.userName,
                    `"${(user.userDetail.password || "").split('"').join('""')}"`
                ];
                mat.push(data);
                if (user.userServices.length > 0) {
                    user.userServices
                        .filter(service => service.active === "true" && !service.ssoEnabled)
                        .forEach((service) => {
                            if (!mat[0][mat[index + 1].length + 2]) {
                                mat[0] = [
                                    ...mat[0],
                                    "Service",
                                    "Username",
                                    "Password",
                                ]
                            }
                            mat[index + 1] = [
                                ...mat[index + 1],
                                service.serviceDisplayName,
                                service.externalUserName,
                                service.externalUserPassword,
                            ]
                        });

                }
            });
            for (let i = 0; i < mat.length; i++) {
                for (let j = 0; j < mat[0].length; j++) {
                    if (!mat[i][j]) {
                        mat[i][j] = "N/A"
                    }
                }
                csvContent += mat[i].join(",") + "\r\n";
            }
            var encodedUri = `data:text/csv;charset=utf-8,${encodeURIComponent(universalBOM + csvContent)}`;
            var link = document.createElement("a");
            link.setAttribute("id", "bulk-user-download-link");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `User-Cards-${moment().format("YYYY-MM-DD")}.csv`);
            document.body.appendChild(link); // Required for FF
            this.setState({ loading: false }, () => {
                link.click();
                // REMOVING LINK CREATED ELEMENT
                document.body.removeChild(document.querySelector("#bulk-user-download-link"));
            });
        } catch (e) {
            // eslint-disable-next-line no-console -- export failed; log aids debugging
            console.error(e);
        }
    };

    delay = (t) => new Promise((resolve) => setTimeout(resolve, t));


    /**
     * @name exportAsPdf
     * @desc Exports entire data as set of PDF files in one single ZIP file.
     * @return {void}
     */
    exportAsPdf = () => {

        setTimeout(() => {
            // GENERATING PDF
            const link = (
                <PDFDownloadLink document={<Pdf users={this.state.users} fileName={`User-Cards-${moment().format("YYYY-MM-DD")}.pdf`} title={"Learner Cards"} />}>
                    {({ blob, url, loading, error }) => {
                        if (url && !loading) {
                            const element = document.createElement('a');
                            element.setAttribute('href', url);
                            element.setAttribute('download', `User-Cards-${moment().format("YYYY-MM-DD")}.pdf`);

                            element.style.display = 'none';
                            document.body.appendChild(element);

                            element.click();

                            document.body.removeChild(element);

                            this.setState({
                                loading: false
                            });
                        }
                    }}
                </PDFDownloadLink>
            );
            const elem = document.querySelector("#pdfDownloadLinkContainer");
            hydrate(link, elem)
        }, 50);
    }


    /**
 * @name exportAsWord
 * @desc Exports entire data as set of Word files in one single ZIP file.
 * @return {void}
 */
    exportAsWord = async () => {
        const doc = await Word(this.state.users);
        this.setState({ loading: false }, () => {
            Packer.toBuffer(doc).then((buffer) => {
                var blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
                saveAs(blob, `User-Cards-${moment().format("YYYY-MM-DD")}.docx`);

            });
        });
    }

    /**
     * @name handleClose
     * @desc Handles all clean up tasks while closing form.
     * @return {void}
     */
    handleClose = () => {
        this._fetchGeneration += 1;
        this.setGlobal({
            isLoading: false,
        });
        this.props.onClose();
        this.cacheByUserId = new Map();
        this.cacheUserData = null;
        this.setState({
            users: [],
            viewType: this.VIEW_TYPES.READ_ONLY,
            isFetchingCards: false,
            cardsFetchFinished: false,
            cardsLoadError: null,
            isCompleted: false,
            loaderText: "Please wait, content is being downloaded!",
        });
        if (this.formRef) {
            this.formRef.resetForm();
        };
    };

    get isDownloadCompleted() {
        return this.state.cardsFetchFinished && this.state.users.length > 0;
    }

    getAlertSeverity() {
        if (this.state.cardsLoadError && this.state.users.length === 0) {
            return "error";
        }
        if (this.state.isCompleted) {
            return "success";
        }
        return "warning";
    }

    render() {
        return (
            <Dialog open={this.props.isOpen} onClose={() => this.handleClose()}>
                <div id="pdfDownloadLinkContainer"></div>
                <Loader isLoading={this.state.loading} />
                {!this.global.isLoading && (
                    <>
                        <AppBar style={{
                            position: 'relative'
                        }}>
                            <Toolbar>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    width="100%"
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                    >
                                        <IconButton
                                            id="user-card-close-btn"
                                            edge="start" color="inherit" onClick={() => this.handleClose()} aria-label="close">
                                            <CloseIcon />
                                        </IconButton>
                                        <Typography variant="h6" >
                                            {this.state.cardsFetchFinished &&
                                            this.props.users.length > 0 &&
                                            this.state.users.length !== this.props.users.length
                                                ? `${this.state.users.length} / ${this.props.users.length} `
                                                : ""}
                                            {this.state.users.length} Learner Card{this.state.users.length !== 1 ? "s" : ""}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        {this.state.viewType === this.VIEW_TYPES.READ_ONLY
                                            &&
                                            <IconButton
                                                onClick={() => this.toggleView()}
                                                type="button"
                                                style={{
                                                    color: 'white'
                                                }}>
                                                <EditIcon />
                                            </IconButton>
                                        }
                                        {this.state.viewType === this.VIEW_TYPES.EDITABLE
                                            &&
                                            <IconButton
                                                onClick={
                                                    () => {
                                                        this.formRef.setFieldTouched("users", true);
                                                        document.querySelector("#form-submit-btn").click();
                                                    }
                                                }
                                                type="submit"
                                                style={{
                                                    color: 'white'
                                                }}>
                                                <SaveIcon />
                                            </IconButton>
                                        }
                                        <Menu
                                            buttonIcon={<GetAppIcon style={{ color: 'white' }} />}
                                            options={[
                                                {
                                                    title: "Export As CSV",
                                                    onClick: () => this.setState({ triggerDownload: true, loading: true, downloadType: CSV })
                                                },
                                                {
                                                    title: "Export As PDF",
                                                    onClick: () => this.setState({ triggerDownload: true, loading: true, downloadType: PDF })
                                                },
                                                {
                                                    title: "Export As Word",
                                                    onClick: () => this.setState({ triggerDownload: true, loading: true, downloadType: WORD })
                                                }
                                            ]}
                                        >
                                        </Menu>
                                    </Box>
                                </Box>

                            </Toolbar>
                        </AppBar>

                        <Alert
                            icon={
                                this.state.isCompleted ||
                                (this.state.cardsFetchFinished && !this.state.isFetchingCards)
                                    ? null
                                    : <CircularProgress color="warning" size={20} />
                            }
                            severity={this.getAlertSeverity()}
                        >
                            {this.state.loaderText}
                        </Alert>
                        <DialogContent style={{
                            padding: 0
                        }}>
                            <Formik
                                validate={this.validateForm}
                                innerRef={ref => this.formRef = ref}
                                initialValues={{
                                    users: Object.assign([], this.state.users)
                                }}
                                onSubmit={this.handleSubmit}
                            >
                                {
                                    ({ values, setFieldValue, setFieldTouched, errors, touched, handleSubmit }) => (
                                        <form
                                            id="user-form"
                                            onSubmit={handleSubmit}
                                            noValidate
                                            autoComplete="off"
                                        >
                                            {/* DUE TO FORMIK AND
                                            MATERIAL UI CONFLICT
                                            HIDDEN SUBMIT BUTTON
                                            IS BEING USED */}
                                            <input type="submit" style={{
                                                display: "none"
                                            }} id="form-submit-btn" />
                                            <div style={{ minWidth: 580 }}>
                                                <Grid container >
                                                    {this.isDownloadCompleted && (
                                                        <>
                                                            <Grid item xs={12}>
                                                                <Box
                                                                    width="100%"
                                                                    display="flex"
                                                                    justifyContent="space-between"
                                                                    component="div"
                                                                    alignItems="center"
                                                                >
                                                                    <img style={{ margin: '10px 0px 0px 25px' }} alt="logo" width="100px" src="https://content.connectedcorrections.com/assets/logo/orijin-logo.svg" />
                                                                </Box>
                                                            </Grid>
                                                            {this.state.viewType === this.VIEW_TYPES.READ_ONLY && (
                                                                <ReadView
                                                                    users={this.state.users}
                                                                />
                                                            )}
                                                            {this.state.viewType === this.VIEW_TYPES.EDITABLE && (
                                                                <EditView
                                                                    setFieldTouched={setFieldTouched}
                                                                    setFieldValue={setFieldValue}
                                                                    users={values.users}
                                                                    errors={errors}
                                                                    touched={touched}
                                                                    fieldChangeDetector={this.fieldChangeDetector}
                                                                />
                                                            )}
                                                        </>
                                                    )}

                                                </Grid>
                                            </div>

                                        </form>
                                    )
                                }
                            </Formik>
                        </DialogContent>
                    </>
                )}
            </Dialog>
        );
    };
};

BulkUserCard.propTypes = {
    fetchUsers: PropTypes.func,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    setResponse: PropTypes.func.isRequired,
    users: PropTypes.arrayOf(
        PropTypes.shape({
            personId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        })
    ).isRequired,
};

export default BulkUserCard;